import { useSelector } from 'react-redux';

import { RootState } from '../../../store/store';

/**
 * Identity props for the shared `TopBar`, derived from real auth state.
 *
 * DEF-004: every screen used to hard-code `avatarInitials="CA"`,
 * `accountName="Mi cuenta"` and `savedCount={3}`, so anonymous visitors were
 * shown a signed-in account pill and a fabricated "3 saved" badge. TopBar
 * already supports the anonymous state correctly — it renders the "Ingresar"
 * action when `avatarInitials` is undefined — the screens simply never used it.
 *
 * `commodity/` deliberately does not import from `core/`, so TopBar stays a
 * dumb presentational component and screens spread this hook into it:
 *
 *   const chrome = useAuthChrome();
 *   <TopBar {...chrome} nav={BUYER_NAV} />
 */
export interface AuthChrome {
  /** Initials for the account pill; undefined when signed out. */
  avatarInitials?: string;
  /** Display name beside the avatar; undefined when signed out. */
  accountName?: string;
}

const initials = (firstName?: string, lastName?: string, email?: string): string => {
  const first = firstName?.trim()?.[0];
  const last = lastName?.trim()?.[0];
  if (first && last) return `${first}${last}`.toUpperCase();
  if (first) return first.toUpperCase();
  return (email?.trim()?.[0] ?? '?').toUpperCase();
};

export const useAuthChrome = (): AuthChrome => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  if (!isAuthenticated || !user) return {};

  return {
    avatarInitials: initials(user.firstName, user.lastName, user.email),
    accountName: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
  };
};

export default useAuthChrome;
